import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Founding Account Executive at SELECT\ndescription: SELECT Founding AE job posting\ndate: 2024-12-26T12:00:00.000Z\n---\n\n**Role Type:** Full-time\n\n**Location:** Fully Remote (Canada or US)\n\n## About the role\n\nManaging and operating a modern data warehouse is hard. Data platform teams are responsible for onboarding thousands of users across their company and empowering them with tooling and processes to harness their companies data. These teams have to monitor and audit thousands of data assets, ensure they are correctly secured and discoverable within the organization, and implement controls and monitoring to ensure costs don't spiral out of control.\n\nSELECT started out by solving the most pressing of these challenges: managing and optimizing their data warehouse costs. We've grown quickly and are now serving over 150 customers including Vimeo, Intercom, Substack, ClickUp and more.\n\nSELECT is hiring our first Account Executive role to help accelerate our sales.\n\n\n## Responsibilities\n\nAs an early sales hire, you will:\n\n- Source, identify and qualify leads in the Snowflake ecosystem and develop them into high-value opportunities\n- Reach prospective customers in any way possible, including cold calling\n- Work with modern GTM tooling like Clay to automate sales research processes and target potential customers with highly personalized messaging\n- Close opportunities directly with the founders\n- Attend in person events through the year across the United States and (occassionally) United Kingdom\n- Own building a repeatable sales playbook and planning for the next stage of growth of SELECT\n- Keep our CRM up to date with customer information and pipeline\n- Collaborate with engineering and product to identify new features and investments areas, based on customer feedback\n\n## Ideal Qualities\n\n- Based in Canada or the US (with preference for EST timezone) \n- Have 3-4+ years of relevant industry experience in a Sales Development Representative and/or Account Executive role, selling to SMBs and Startups in a high velocity fashion\n- Proven track-record of overachievement against team & company goals\n- Passion to drive sales via strategic outbound sales motions, using email, phone & LinkedIn\n- Are excited to build a high-potential business from scratch, including establishing our foundations for growth\n- Are comfortable navigating ambiguous challenges and adopting a \"big picture\" approach to sales, including closely collaborating with our founders\n- Strong written and verbal skills\n- Tenacity - willingness to hunt for aspirational logos and be resourceful in identifying key decision makers\n- Great attitude and willing to fail fast to learn and iterate\n- Strong organizational skills (meticulous with data)\n\n## Nice to have\n\n* Experience working in an early-stage startup environment\n* Experience working in the modern data stack\n* Experience with the problems we're solving (FinOps and cloud infrastructure [cost management](/posts/snowflake-cost-management), data warehouse management and observability)\n\n\n## Application process\n\n* The process is fully remote and all communication will happen over email or via video chat.\n* Upon successful review of your resume, you will be contacted to proceed with our interview process:\n    * Round 1: General Fit Interview\n    * Round 2: 1:1 Interviews with Core Team (Company Fit, Sales Experience and Potential, Cross Functional Fit)\n    * Round 3: Written Exercise Review & Sales Presentation to Leadership Team\n* We aim to screen, interview, make a decision, send and finalize an offer within 2 weeks of application.\n\nEmail us at [hello@select.dev](mailto:hello@select.dev) if you're interested in learning more about the position & company.\n";
const filepath = "/account-executive.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
